import React, { useMemo } from "react";
import BaseChart from "../../components/ui/BaseChart";

const fullUsd = (n) =>
  `$${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const compactUsd = (n) => {
  const v = Number(n ?? 0);
  if (Math.abs(v) >= 1000) return `${v < 0 ? "-" : ""}$${(Math.abs(v) / 1000).toFixed(1)}k`;
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(0)}`;
};

const POSITIVE = "#16A34A";
const NEGATIVE = "#EF4444";

// Join revenue & expense breakdowns by teamId → profit = revenue − expense.
const mergeProfit = (revenue = [], expense = []) => {
  const map = new Map();
  const keyOf = (e) => String(e.teamId ?? "__none__");
  (revenue ?? []).forEach((r) => {
    map.set(keyOf(r), { name: r.name, color: r.color, revenue: r.amountUsd, expense: 0 });
  });
  (expense ?? []).forEach((e) => {
    const k = keyOf(e);
    const cur = map.get(k) ?? { name: e.name, color: e.color, revenue: 0, expense: 0 };
    cur.expense = e.amountUsd;
    if (!cur.color) cur.color = e.color;
    map.set(k, cur);
  });
  return [...map.values()].map((v) => ({
    ...v,
    profit: Math.round((v.revenue - v.expense) * 100) / 100,
  }));
};

const ProfitByTeamChart = ({ revenueBreakdown, expenseBreakdown }) => {
  const { categories, points, total } = useMemo(() => {
    const merged = mergeProfit(revenueBreakdown, expenseBreakdown)
      .filter((m) => m.revenue !== 0 || m.expense !== 0)
      .sort((a, b) => b.profit - a.profit);
    return {
      categories: merged.map((m) => m.name),
      points: merged.map((m) => ({
        y: m.profit,
        // Match the team's color from the Revenue by Team chart.
        color: m.color ?? (m.profit >= 0 ? POSITIVE : NEGATIVE),
        revenue: m.revenue,
        expense: m.expense,
      })),
      total: Math.round(merged.reduce((a, m) => a + m.profit, 0) * 100) / 100,
    };
  }, [revenueBreakdown, expenseBreakdown]);

  const chartOptions = useMemo(() => {
    if (!points.length) return null;
    return {
      chart: {
        type: "bar",
        height: Math.max(200, points.length * 46 + 40),
        spacing: [10, 16, 6, 6],
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
      },
      title: { text: null },
      xAxis: {
        categories,
        lineColor: "#e5e7eb",
        tickLength: 0,
        labels: { style: { fontSize: "11px", color: "#475569", fontWeight: "500" } },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#f3f4f6",
        plotLines: [{ value: 0, color: "#cbd5e1", width: 1, zIndex: 3 }],
        labels: {
          formatter() {
            return compactUsd(this.value);
          },
          style: { fontSize: "10px", color: "#9ca3af" },
        },
      },
      legend: { enabled: false },
      tooltip: {
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc", fontSize: "11px" },
        borderWidth: 0,
        borderRadius: 6,
        useHTML: true,
        pointFormatter() {
          const word = this.y >= 0 ? "Profit" : "Loss";
          return `<span style="color:${this.color}">●</span> <b>${this.category}</b><br/>${word} ${fullUsd(this.y)}<br/><span style="opacity:.7">Rev ${fullUsd(this.revenue)} · Exp ${fullUsd(this.expense)}</span>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          borderWidth: 0,
          pointPadding: 0.08,
          groupPadding: 0.08,
          dataLabels: {
            enabled: true,
            formatter() {
              return compactUsd(this.y);
            },
            style: { fontSize: "10px", fontWeight: "600", color: "#475569", textOutline: "none" },
          },
        },
      },
      series: [{ name: "Profit", data: points }],
    };
  }, [points, categories]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">Profit by Team</h4>
          <p className="text-[11px] text-gray-400 mt-0.5">Revenue − Expenses per team · USD</p>
        </div>
        {points.length > 0 && (
          <span className="text-[11px] text-gray-400">
            Net{" "}
            <span className={`font-semibold ${total >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              {fullUsd(total)}
            </span>
          </span>
        )}
      </div>

      {chartOptions ? (
        <BaseChart options={chartOptions} className="w-full" />
      ) : (
        <p className="text-sm text-gray-400 py-16 text-center">No profit data for this period.</p>
      )}
    </div>
  );
};

export default ProfitByTeamChart;
